import { observer } from 'mobx-react-lite'
import { capitalizeWords, DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import {
  Box,
  EmptyCell,
  type IAvatarInfo,
  Icon,
  NameValueRecord,
  Status,
  Typography,
} from 'shared/ui'
import { type IColumn } from 'shared/ui/TableNew'
import { getCellForNameDefault } from 'shared/helpers/getCellForNameDefault'
import { secondsToTime } from 'shared/lib/secondsToTime'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type RowCreditUsage } from 'entities/Analytics/model/Table/RowCreditUsage'
import {
  type IContactDetails,
  type IResponseCreditUsageAnalyticsDetailsItem,
} from 'entities/Analytics/api/types'
import { type TabType } from 'pages/analytics/pages/creditUsage/store/creditUsageTableStore'
import { creditUsageStatusConfig } from './creditUsageStatusConfig'
import styles from './styles.module.scss'

export const getTableColumns = (tabType: TabType): IColumn<RowCreditUsage>[] => {
  const commonColumns: IColumn<RowCreditUsage>[] = [
    {
      field: 'processed_at',
      name: 'Date',
      renderRowCell: (row) =>
        row.origin.processed_at ? (
          <span aria-label='CreditUsageDate'>
            {uiStore.dayjs(row.origin.processed_at).format(DayjsFormats.fullWithAtDash2)}
          </span>
        ) : (
          <EmptyCell />
        ),
    },
    {
      field: 'details.from.number',
      name: 'From',
      maxWidth: '280px',
      renderRowCell: (row) => <FromToCell item={row.origin.details?.from} />,
    },
    {
      field: 'details.to.number',
      name: 'To',
      maxWidth: '300px',
      renderRowCell: (row) => <FromToCell item={row.origin.details?.to} />,
    },
    {
      field: 'details.type',
      name: 'Type',
      renderRowCell: (row) => {
        return row.direction ? (
          <Box display={'flex'} alignItems={'center'} gap={'4px'}>
            <Icon icon={row.directionIcon} color='var(--content-primary-tertiary)' />
            {capitalizeWords(row.direction)}
          </Box>
        ) : (
          <EmptyCell />
        )
      },
    },
    {
      field: 'amount',
      name: 'Credits',
      renderRowCell: (row) => row.origin.inversed_amount,
    },
    {
      field: 'details.status',
      name: 'Status',
      renderRowCell: (row) =>
        row.origin.details?.status ? (
          <Status
            className={row.disabled ? styles.disabledStatus : undefined}
            {...creditUsageStatusConfig[row.origin.details.status]}
          />
        ) : (
          <EmptyCell />
        ),
    },
  ]

  switch (tabType) {
    case 'messages':
      return [
        ...commonColumns,
        {
          field: 'details.source',
          name: 'Source',
          renderRowCell: (row) => capitalizeWords(row.origin.details?.source || ''),
        },
      ]
    case 'calls':
      const callsColumns = [...commonColumns]
      callsColumns.splice(5, 0, {
        field: 'details.duration_seconds',
        name: 'Duration',
        renderRowCell: (row) => <DurationCell row={row} />,
      })
      return callsColumns
    case 'chatbots':
      const chatbotsColumns = [...commonColumns]
      chatbotsColumns.splice(1, 0, {
        field: 'chatbot_name',
        name: 'Agent Name',
        maxWidth: '268px',
        renderRowCell: (row) =>
          row.origin.chatbot_name ? (
            <div className={styles.chatbotName}>
              <Typography
                variant={'body-rg-regular'}
                ellipsis
                tooltipProps={{
                  label: row.origin.chatbot_name,
                }}
                ariaLabel={'name'}
              >
                {row.origin.chatbot_name}
              </Typography>
            </div>
          ) : (
            <EmptyCell />
          ),
      })
      return chatbotsColumns
    case 'ai_calling_agent':
      const aiAgentColumns = [...commonColumns]
      aiAgentColumns.splice(1, 0, {
        field: 'agent_name',
        name: 'Agent Name',
        maxWidth: '268px',
        renderRowCell: (row) =>
          row.origin.agent_name ? (
            <div className={styles.chatbotName}>
              <Typography
                variant={'body-rg-regular'}
                ellipsis
                tooltipProps={{
                  label: row.origin.agent_name,
                }}
                ariaLabel={'name'}
              >
                {row.origin.agent_name}
              </Typography>
            </div>
          ) : (
            <EmptyCell />
          ),
      })
      aiAgentColumns.splice(6, 0, {
        field: 'details.duration_seconds',
        name: 'Duration',
        renderRowCell: (row) => <DurationCell row={row} />,
      })
      return aiAgentColumns
    case 'transcripts':
      const transcriptsColumns = [...commonColumns]
      transcriptsColumns.splice(5, 0, {
        field: 'details.duration_seconds',
        name: 'Duration',
        renderRowCell: (row) => <DurationCell row={row} />,
      })
      return transcriptsColumns.filter((column) => column.field !== 'details.type')
    default:
      return commonColumns
  }
}

const DurationCell = observer(({ row }: { row: RowCreditUsage }) => {
  return row.origin.details?.duration_seconds ? (
    <span>{secondsToTime(row.origin.details?.duration_seconds)}</span>
  ) : (
    <EmptyCell />
  )
})

const FromToCell = observer(
  ({ item }: { item?: IResponseCreditUsageAnalyticsDetailsItem['details']['from'] }) => {
    if (item?.type === 'inbox') {
      return <NameValueRecord icon='inboxes2' name={item.name} value={item.formatted_number} />
    }
    if (item?.type === 'contact') {
      return <ContactCell record={item} />
    }
    return <EmptyCell />
  }
)

const ContactCell = observer(({ record }: { record: IContactDetails }) => {
  const { contactsStore } = useEntitiesContext()
  const deletedContactAvatarInfo: IAvatarInfo = {
    icon: 'delete',
    color: 'var(--gray-a-15)',
    tooltipProps: {
      placement: 'top',
      label: 'Deleted contact',
    },
  }

  return getCellForNameDefault({
    fullName: record.name || '',
    avatarInfo: record.is_archived
      ? deletedContactAvatarInfo
      : contactsStore.getItem(record.contact_id)?.avatarInfo ?? {
          image: record.photo_url || '',
          color: record.color || '',
          firstName: record.name || '',
          number: record.number,
        },
    size: 16,
    fontSize: 9,
    iconProps: {
      fontSize: 10,
    },
    value: record.formatted_number,
  })
})
